import { Community, Forum, Incident, Message, Payment, Poll, Reservation, CommonArea, User } from '../types';

const findById = <T extends { id: string }>(items: T[], id: string): T | undefined =>
  items.find((item) => item.id === id);

export const selectCommunity = (communities: Community[], id: string): Community | undefined =>
  findById(communities, id);

export const selectCommunityMembers = (communities: Community[], communityId: string): User[] =>
  selectCommunity(communities, communityId)?.members ?? [];

export const selectIncident = (incidents: Incident[], id: string): Incident | undefined =>
  findById(incidents, id);

export const selectCommunityIncidents = (incidents: Incident[], communityId: string): Incident[] =>
  incidents.filter((incident) => incident.communityId === communityId);

export const selectPayment = (payments: Payment[], id: string): Payment | undefined =>
  findById(payments, id);

export const selectCommunityPayments = (payments: Payment[], communityId: string): Payment[] =>
  payments.filter((payment) => payment.communityId === communityId);

export const selectUserPayments = (payments: Payment[], userId: string): Payment[] =>
  payments.filter((payment) => payment.userId === userId);

export const selectPoll = (polls: Poll[], id: string): Poll | undefined =>
  findById(polls, id);

export const selectCommunityPolls = (polls: Poll[], communityId: string): Poll[] =>
  polls.filter((poll) => poll.communityId === communityId);

export const hasUserVotedInPoll = (polls: Poll[], pollId: string, userId: string): boolean =>
  selectPoll(polls, pollId)?.voters?.includes(userId) ?? false;

export const selectReservation = (reservations: Reservation[], id: string): Reservation | undefined =>
  findById(reservations, id);

export const selectUserReservations = (reservations: Reservation[], userId: string): Reservation[] =>
  reservations.filter((reservation) => reservation.userId === userId);

export const selectAreaReservations = (reservations: Reservation[], areaId: string): Reservation[] =>
  reservations.filter((reservation) => reservation.commonAreaId === areaId);

export const selectDayReservations = (
  reservations: Reservation[],
  areaId: string,
  date: string,
): Reservation[] =>
  reservations.filter(
    (reservation) => reservation.commonAreaId === areaId && reservation.date === date,
  );

export const selectCommonArea = (commonAreas: CommonArea[], id: string): CommonArea | undefined =>
  findById(commonAreas, id);

export const selectCommunityMessages = (messages: Message[], communityId: string): Message[] =>
  messages.filter((message) => message.communityId === communityId);

export const selectForumMessages = (messages: Message[], forumId: string): Message[] =>
  messages.filter((message) => message.forumId === forumId);

export const selectUserPrivateMessages = (messages: Message[], userId: string): Message[] =>
  messages.filter((message) => message.receiverId === userId || message.senderId === userId);

export const selectConversation = (
  messages: Message[],
  user1Id: string,
  user2Id: string,
): Message[] =>
  messages.filter(
    (message) =>
      (message.senderId === user1Id && message.receiverId === user2Id) ||
      (message.senderId === user2Id && message.receiverId === user1Id),
  );

export const selectCommunityForums = (forums: Forum[], communityId: string): Forum[] =>
  forums.filter((forum) => forum.communityId === communityId);
